// Constantes de conversión
const CONVERSION_RATES = {
  L: 1, // Litros (unidad base)
  GAL: 0.264172, // Galones
  M3: 0.001, // Metros cúbicos
  CUBIC_FT: 0.0353147, // Pies cúbicos
};

// Precios por unidad (ejemplo)
const PRICES_PER_UNIT = {
  L: 0.01, // $0.01 por litro
  GAL: 0.0378541, // $0.0378541 por galón
  M3: 10, // $10 por metro cúbico
  CUBIC_FT: 0.283168, // $0.283168 por pie cúbico
};

// Convertir de litros a la unidad seleccionada
export const convertFromLiters = (liters, targetUnit) => {
  if (!CONVERSION_RATES[targetUnit]) return liters;
  return liters * CONVERSION_RATES[targetUnit];
};

// Convertir a litros desde la unidad seleccionada
export const convertToLiters = (value, fromUnit) => {
  if (!CONVERSION_RATES[fromUnit]) return value;
  return value / CONVERSION_RATES[fromUnit];
};

// Calcular el costo basado en la unidad
export const calculateCost = (value, unit) => {
  if (!PRICES_PER_UNIT[unit]) return value * PRICES_PER_UNIT.L;
  return value * PRICES_PER_UNIT[unit];
};

// Formatear el valor según la unidad
export const formatValue = (value, unit) => {
  const formattedValue = Number(value).toFixed(2);
  switch (unit) {
    case 'L':
      return `${formattedValue} L`;
    case 'GAL':
      return `${formattedValue} gal`;
    case 'M3':
      return `${formattedValue} m³`;
    case 'CUBIC_FT':
      return `${formattedValue} ft³`;
    default:
      return `${formattedValue} L`;
  }
};

// Obtener el símbolo de la unidad
export const getUnitSymbol = (unit) => {
  switch (unit) {
    case 'L':
      return 'L';
    case 'GAL':
      return 'gal';
    case 'M3':
      return 'm³';
    case 'CUBIC_FT':
      return 'ft³';
    default:
      return 'L';
  }
};

// Opciones de unidades disponibles
export const AVAILABLE_UNITS = [
  { value: 'L', label: 'Litros (L)' },
  { value: 'GAL', label: 'Galones (gal)' },
  { value: 'M3', label: 'Metros cúbicos (m³)' },
  { value: 'CUBIC_FT', label: 'Pies cúbicos (ft³)' }
];

// ============================================
// TARIFAS SADM 2025 - Monterrey, México
// Sistema: Tarifa base de agua + Drenaje (25%) + Saneamiento (variable) + IVA (16%)
// ============================================

// ============================================
// TARIFA ÚNICA: GUBERNAMENTAL
// ============================================
export const AVAILABLE_TARIFFS = [
  { value: 'governmental', label: 'Gubernamental Cat. 10', description: 'Tarifa única del sistema' }
];

// Tarifa gubernamental simplificada (solo consumo de agua)
const GOVERNMENTAL_WATER_RATE = 38.09; // MXN por m³


// Calcular el costo de agua en pesos mexicanos según tarifas escalonadas y cargo fijo
export function calculateWaterBill(liters) {
  // Convertir litros a metros cúbicos
  const m3 = liters / 1000;
  let variableCharge = 0;

  if (m3 <= 6) {
    variableCharge = 67.68;
  } else if (m3 <= 10) {
    variableCharge = 80.51;
  } else if (m3 <= 200) {
    variableCharge = 93.06;
  } else {
    // Para consumos mayores a 200 m³
    variableCharge = 93.06 + (m3 - 200) * 127.07;
  }

  // Cargo fijo
  const fixedCharge = 439.22;
  const total = variableCharge + fixedCharge;
  return {
    total,
    variableCharge,
    fixedCharge,
    m3: Number(m3.toFixed(2))
  };
}

// Calcular el costo para cualquier unidad
export function calculateWaterBillForUnit(value, unit) {
  // Convertir a litros primero
  let liters = value;
  switch (unit) {
    case 'L':
      break;
    case 'GAL':
      liters = value / CONVERSION_RATES.GAL;
      break;
    case 'M3':
      liters = value * 1000;
      break;
    case 'CUBIC_FT':
      liters = value / CONVERSION_RATES.CUBIC_FT;
      break;
    default:
      break;
  }
  return calculateWaterBill(liters);
}

// Calcular el costo de agua por m2 según tarifa seleccionada (DEPRECADA)
// Usar calculateHousingBillByLiters() para cálculos más precisos
export function calculateHousingBill(m2, tariffType = 'domestic') {
  console.warn('calculateHousingBill está deprecada, usar calculateHousingBillByLiters()');
  return {
    total: 0,
    rate: 0,
    m2: Number(m2),
    deprecated: true
  };
}

/**
 * Calcula el costo de agua por litros consumidos usando SOLO tarifa gubernamental
 * SIMPLIFICADO: Solo calcula el costo del consumo de agua (sin drenaje, saneamiento, IVA)
 * @param {number} liters - Litros consumidos
 * @param {string} tariffType - Ignorado, siempre usa gubernamental
 * @returns {Object} Costo simplificado del consumo
 */
export function calculateHousingBillByLiters(liters, tariffType = 'governmental') {
  if (!liters || liters < 0) {
    return {
      total: 0,
      cubicMeters: 0,
      rate: GOVERNMENTAL_WATER_RATE,
      tariffType: 'governmental',
      tariffLabel: 'Gubernamental Cat. 10',
      error: 'Consumo inválido'
    };
  }

  // Convertir litros a metros cúbicos
  const cubicMeters = liters / 1000;
  
  // Calcular SOLO el costo de agua (tarifa lineal)
  const waterCost = cubicMeters * GOVERNMENTAL_WATER_RATE;

  return {
    total: Number(waterCost.toFixed(2)),
    cubicMeters: Number(cubicMeters.toFixed(2)),
    rate: GOVERNMENTAL_WATER_RATE,
    tariffType: 'governmental',
    tariffLabel: 'Gubernamental Cat. 10'
  };
}

// Calcular predicción inteligente de factura para el próximo mes
export function calculateNextMonthBillPrediction(currentMonthConsumption, daysElapsed, tariffType = 'domestic') {
  // Si han pasado menos de 5 días, no hay suficientes datos para una predicción confiable
  if (daysElapsed < 5) {
    return {
      predictedConsumption: 0,
      predictedBill: 0,
      confidence: 'low',
      message: 'Necesitamos más datos para una predicción confiable'
    };
  }

  // Calcular el promedio diario de consumo
  const dailyAverage = currentMonthConsumption / daysElapsed;
  
  // Obtener días del próximo mes (aproximación de 30 días)
  const daysInNextMonth = 30;
  
  // Aplicar factores de ajuste basados en patrones de consumo
  let adjustmentFactor = 1.0;
  
  // Ajuste estacional (aproximado)
  const currentMonth = new Date().getMonth();
  if (currentMonth >= 5 && currentMonth <= 8) { // Verano (junio-septiembre)
    adjustmentFactor = 1.15; // 15% más en verano
  } else if (currentMonth >= 11 || currentMonth <= 1) { // Invierno (diciembre-febrero)
    adjustmentFactor = 0.95; // 5% menos en invierno
  }
  
  // Ajuste por tendencia (si el consumo está acelerando o desacelerando)
  // Comparar la primera mitad con la segunda mitad del periodo actual
  const midPoint = Math.floor(daysElapsed / 2);
  const firstHalfAverage = currentMonthConsumption * 0.4 / midPoint; // Aproximación
  const secondHalfAverage = currentMonthConsumption * 0.6 / (daysElapsed - midPoint); // Aproximación
  
  if (secondHalfAverage > firstHalfAverage * 1.1) {
    adjustmentFactor *= 1.05; // Tendencia al alza
  } else if (secondHalfAverage < firstHalfAverage * 0.9) {
    adjustmentFactor *= 0.95; // Tendencia a la baja
  }
  
  // Calcular consumo predicho
  const predictedConsumption = dailyAverage * daysInNextMonth * adjustmentFactor;
  
  // Calcular factura predicha
  const billCalculation = calculateHousingBillByLiters(predictedConsumption, tariffType);
  
  // Determinar nivel de confianza
  let confidence = 'medium';
  if (daysElapsed >= 20) {
    confidence = 'high';
  } else if (daysElapsed < 10) {
    confidence = 'low';
  }
  
  return {
    predictedConsumption: Math.round(predictedConsumption),
    predictedBill: Math.round(billCalculation.total * 100) / 100,
    billDetails: billCalculation,
    confidence,
    dailyAverage: Math.round(dailyAverage * 100) / 100,
    adjustmentFactor: Math.round(adjustmentFactor * 100) / 100,
    daysAnalyzed: daysElapsed,
    message: confidence === 'high' 
      ? 'Predicción muy confiable basada en patrones de consumo actuales'
      : confidence === 'medium'
      ? 'Predicción moderadamente confiable, seguiremos refinando'
      : 'Predicción preliminar, necesitamos más datos para mayor precisión'
  };
}

// Calcular comparación de predicción con el mes anterior
export function compareWithPreviousMonthBill(predictedBill, previousMonthBill) {
  if (!previousMonthBill || previousMonthBill === 0) {
    return {
      difference: 0,
      percentage: 0,
      trend: 'neutral',
      message: 'No hay datos del mes anterior para comparar'
    };
  }
  
  const difference = predictedBill - previousMonthBill;
  const percentage = Math.round((difference / previousMonthBill) * 100 * 100) / 100;
  
  let trend = 'neutral';
  let message = '';
  
  if (Math.abs(percentage) < 5) {
    trend = 'stable';
    message = 'Tu factura se mantendrá similar al mes anterior';
  } else if (percentage > 0) {
    trend = 'increase';
    message = `Tu factura podría aumentar ${percentage}% respecto al mes anterior`;
  } else {
    trend = 'decrease';
    message = `Tu factura podría reducirse ${Math.abs(percentage)}% respecto al mes anterior`;
  }
  
  return {
    difference: Math.round(difference * 100) / 100,
    percentage,
    trend,
    message
  };
} 